// ============================================================================
// NOTIFICATIONS PAGE
// ============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  FolderOpen,
  Clock,
  DollarSign,
  Megaphone,
  Database,
  Share2,
  CheckCircle,
  ExternalLink,
  RefreshCw,
  AlertTriangle,
  Megaphone as BroadcastIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { DestructiveConfirmModal } from '../../components/common/DestructiveConfirmModal';
import { useNotifications } from '../../hooks/useNotifications';
import { broadcastPlatformUpdate, Notification, NotificationType } from '../../services/notificationsService';
import { useAuth } from '../../contexts/AuthContext';

const TYPE_ICON: Record<NotificationType, React.ElementType> = {
  stale_project: FolderOpen,
  stale_session: Clock,
  weekly_cost_summary: DollarSign,
  platform_update: Megaphone,
  data_retention_request: Database,
  project_shared: Share2,
  session_completed: CheckCircle,
};

const TYPE_COLOUR: Record<NotificationType, string> = {
  stale_project: 'bg-amber-100 text-amber-600',
  stale_session: 'bg-orange-100 text-orange-600',
  weekly_cost_summary: 'bg-blue-100 text-blue-600',
  platform_update: 'bg-primary/10 text-primary',
  data_retention_request: 'bg-red-100 text-red-600',
  project_shared: 'bg-green-100 text-green-600',
  session_completed: 'bg-indigo-100 text-indigo-600',
};

function groupNotifications(notifications: Notification[]) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const weekStart = todayStart - 6 * 24 * 60 * 60 * 1000;

  const today: Notification[] = [];
  const thisWeek: Notification[] = [];
  const older: Notification[] = [];

  for (const n of notifications) {
    const t = new Date(n.createdAt).getTime();
    if (t >= todayStart) today.push(n);
    else if (t >= weekStart) thisWeek.push(n);
    else older.push(n);
  }

  return { today, thisWeek, older };
}

function timeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface NotificationRowProps {
  notification: Notification;
  onMarkRead: (id: string) => Promise<void>;
  onTakeAction: (id: string, action: 'ignored' | 'purged') => Promise<void>;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onMarkRead, onTakeAction }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const Icon = TYPE_ICON[notification.type];
  const colourClass = TYPE_COLOUR[notification.type];

  const handleClick = async () => {
    setExpanded(e => !e);
    if (!notification.read) await onMarkRead(notification.notificationId);
  };

  const handleIgnore = async () => {
    setProcessing(true);
    try {
      await onTakeAction(notification.notificationId, 'ignored');
      toast.success('Notification dismissed');
    } catch {
      toast.error('Failed to dismiss');
    } finally {
      setProcessing(false);
    }
  };

  const handlePurgeConfirm = async () => {
    setProcessing(true);
    try {
      await onTakeAction(notification.notificationId, 'purged');
      setPurgeOpen(false);
      toast.success('Content purged');
    } catch {
      toast.error('Failed to purge content');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <div
        className={`rounded-xl overflow-hidden mb-3 transition-shadow hover:shadow-sm ${
          !notification.read
            ? 'bg-primary/[0.06] border border-primary/25 border-l-[3px] border-l-primary'
            : 'bg-white border border-[#e2e8f0]'
        }`}
      >
        <button
          onClick={handleClick}
          className="w-full px-5 py-4 flex items-start gap-4 text-left"
        >
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colourClass}`}>
            <Icon size={18} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className={`text-sm font-semibold ${notification.read ? 'text-slate-700' : 'text-slate-900'}`}>
                {notification.title}
              </p>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-slate-400">{timeAgo(notification.createdAt)}</span>
                {!notification.read && (
                  <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                )}
              </div>
            </div>
            <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{notification.message}</p>
          </div>
        </button>

        {expanded && (
          <div className="px-5 pb-5 border-t border-[#e2e8f0] pt-4 bg-slate-50">
            <p className="text-sm text-slate-600 mb-4">{notification.message}</p>

            {notification.linkUrl && (
              <button
                onClick={() => navigate(notification.linkUrl!)}
                className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline mb-4"
              >
                <ExternalLink size={14} />
                View
              </button>
            )}

            {notification.type === 'data_retention_request' && !notification.actionTaken && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleIgnore}
                  disabled={processing}
                  className="px-4 py-2 text-sm font-medium border border-[#ced4da] rounded-lg bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Ignore
                </button>
                <button
                  onClick={() => setPurgeOpen(true)}
                  disabled={processing}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Purge data
                </button>
              </div>
            )}

            {notification.type === 'data_retention_request' && notification.actionTaken && (
              <p className="text-sm text-slate-400 italic">
                Action taken: {notification.actionTaken} — {notification.actionTakenAt ? timeAgo(notification.actionTakenAt) : ''}
              </p>
            )}
          </div>
        )}
      </div>

      {purgeOpen && notification.retentionAction && (
        <DestructiveConfirmModal
          isOpen={purgeOpen}
          onClose={() => setPurgeOpen(false)}
          onConfirm={handlePurgeConfirm}
          title={`Purge ${notification.retentionAction.targetType}`}
          message={`This will permanently delete all content for "${notification.retentionAction.targetName}". This cannot be undone.`}
          isProcessing={processing}
        />
      )}
    </>
  );
};

function NotificationGroup({ title, items, onMarkRead, onTakeAction }: {
  title: string;
  items: Notification[];
  onMarkRead: (id: string) => Promise<void>;
  onTakeAction: (id: string, action: 'ignored' | 'purged') => Promise<void>;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mb-8">
      <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">{title}</h2>
      {items.map(n => (
        <NotificationRow key={n.notificationId} notification={n} onMarkRead={onMarkRead} onTakeAction={onTakeAction} />
      ))}
    </div>
  );
}

const NotificationsPage: React.FC = () => {
  const { notifications, unreadCount, loading, error, refetch, markRead, takeAction } = useNotifications();
  const { user } = useAuth();
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastLink, setBroadcastLink] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);

  const { today, thisWeek, older } = groupNotifications(notifications);
  const isEmpty = notifications.length === 0;

  const handleBroadcast = async () => {
    if (!broadcastTitle || !broadcastMessage) return;
    setBroadcastSending(true);
    try {
      await broadcastPlatformUpdate(broadcastTitle, broadcastMessage, broadcastLink || undefined);
      toast.success('Platform update sent');
      setBroadcastOpen(false);
      setBroadcastTitle('');
      setBroadcastMessage('');
      setBroadcastLink('');
      refetch();
    } catch {
      toast.error('Failed to send update');
    } finally {
      setBroadcastSending(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Bell size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Notifications</h1>
              <p className="text-slate-500 text-sm font-medium">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === 'system_admin' && (
              <button
                onClick={() => setBroadcastOpen(true)}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
              >
                <BroadcastIcon size={16} />
                Send update
              </button>
            )}
            <button
              onClick={refetch}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </header>

        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading notifications...</div>
        ) : isEmpty ? (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Bell size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No notifications yet.</p>
            <p className="text-slate-400 text-xs mt-2">Platform updates and activity alerts will appear here.</p>
          </div>
        ) : (
          <>
            <NotificationGroup title="Today" items={today} onMarkRead={markRead} onTakeAction={takeAction} />
            <NotificationGroup title="This week" items={thisWeek} onMarkRead={markRead} onTakeAction={takeAction} />
            <NotificationGroup title="Older" items={older} onMarkRead={markRead} onTakeAction={takeAction} />
          </>
        )}

      </div>

      {/* Broadcast modal (system_admin only) */}
        {broadcastOpen && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}
            onClick={() => setBroadcastOpen(false)}
          >
            <div
              style={{ background: 'white', borderRadius: '12px', maxWidth: '480px', width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <BroadcastIcon size={20} color="#00c7c4" />
                <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0 }}>Send platform update</h2>
              </div>
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#495057', display: 'block', marginBottom: '6px' }}>Title</label>
                  <input
                    value={broadcastTitle}
                    onChange={e => setBroadcastTitle(e.target.value)}
                    placeholder="e.g. New feature: participant view"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#495057', display: 'block', marginBottom: '6px' }}>Message</label>
                  <textarea
                    value={broadcastMessage}
                    onChange={e => setBroadcastMessage(e.target.value)}
                    rows={4}
                    placeholder="Describe the update..."
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#495057', display: 'block', marginBottom: '6px' }}>Link (optional)</label>
                  <input
                    value={broadcastLink}
                    onChange={e => setBroadcastLink(e.target.value)}
                    placeholder="/projects or https://..."
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div style={{ padding: '16px 24px', borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f8f9fa' }}>
                <button
                  onClick={() => setBroadcastOpen(false)}
                  style={{ padding: '8px 16px', background: 'white', border: '1px solid #ced4da', borderRadius: '6px', fontSize: '14px', fontWeight: '500', color: '#495057', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBroadcast}
                  disabled={!broadcastTitle || !broadcastMessage || broadcastSending}
                  style={{ padding: '8px 16px', background: (!broadcastTitle || !broadcastMessage || broadcastSending) ? '#adb5bd' : '#00c7c4', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: (!broadcastTitle || !broadcastMessage || broadcastSending) ? 'not-allowed' : 'pointer' }}
                >
                  {broadcastSending ? 'Sending...' : 'Send to organisation'}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};



export default NotificationsPage;
